import React from "react";
import { Font, View, Text, Document, Page, Image } from "@react-pdf/renderer";
import { styles } from "./style";
import moment from "moment";
import { decrypt } from "../../utils";

const NoteReportPDF = ({
  noteEntries,
  allPatients,
  reportInfo,
  allStaffs,
  signatures,
}) => {
  Font.register({
    family: "Helvetica",
    fonts: [
      {
        src: "Helvetica",
      },
    ],
  });

  // Helper function to check if a field has valid content
  const hasValidContent = (field) => {
    return field && field.toString().trim() !== "";
  };

  // before return
  // const validNoteEntries = noteEntries?.filter((entry) => {
  //   const hasDescription = entry?.fields?.["Description"]?.trim();
  //   const hasGoals = entry?.goals && entry.goals.length > 0;
  //   const hasEssentialSupport =
  //     entry?.essentialSupport && entry.essentialSupport.length > 0;
  //   const hasDescriptions =
  //     entry?.descriptions && entry.descriptions.length > 0;
  //   return hasDescription || hasGoals || hasEssentialSupport || hasDescriptions;
  // });

  const renderNoteEntry = (entry, index) => {
    const signatureData = signatures?.find(
      (sig) =>
        (sig?.staff && sig?.staff === entry?.fields?.["Staff Name"]) ||
        (sig?.admin && sig?.admin === entry?.fields?.["Staff Name"])
    );
    const isCustomNote = entry?.noteId?.general === false;

    return (
      <View key={entry?.id || index} style={styles.noteEntry}>
        {/* Header */}
        <View style={styles.noteHeader}>
          <Text style={styles.clientInfo}>
            Client:{" "}
            {allPatients?.find(
              (patient) =>
                patient.id === entry?.fields?.["Client Name"] ||
                entry?.fields?.["Patient Name"]
            )?.name || "None"}
          </Text>
          <Text style={styles.dateInfo}>
            {moment(entry?.noteDate).format("MM/DD/YYYY")}
          </Text>
        </View>

        {/* Outcome/Goal Section */}
        {hasValidContent(entry.outcome) && (
          <View style={styles.outcomeSection}>
            <View style={styles.outcomeRow}>
              <Text style={styles.outcomeLabel}>Outcome/Goal</Text>
              <Text style={styles.outcomeContent}>{entry.outcome}</Text>
            </View>
          </View>
        )}

        {/* Objective/Behavior Section */}
        {hasValidContent(entry.objective) && (
          <View style={styles.outcomeSection}>
            <View style={styles.outcomeRow}>
              <Text style={styles.outcomeLabel}>Objective/Behavior</Text>
              <Text style={styles.outcomeContent}>{entry.objective}</Text>
            </View>
          </View>
        )}

        {/* {hasValidContent(entry.fields["Description"]) && ( */}
        {!!entry?.fields?.["Description"]?.trim() &&
          ((entry?.supportPlan === "no" &&
            entry?.interventionPlanToggle === "no") ||
            (entry?.supportPlan === "no" &&
              entry?.essentialSupportToggle === "no") ||
            (!entry?.interventionPlanToggle &&
              !entry?.essentialSupportToggle &&
              entry?.supportPlan === "no")) && (
            <View style={styles.contentSection}>
              <View style={styles.sectionRow}>
                <Text style={styles.sectionLabel}>Description</Text>
              </View>
              <View style={styles.sectionRow}>
                <Text style={styles.sectionContent}>
                  {entry.fields["Description"] || "None"}
                </Text>
              </View>
            </View>
          )}

        {/* Custom Note Fields */}
        {isCustomNote &&
          entry?.fields?.noteid?.custom
            ?.filter((item) => {
              const excludedFields = [
                "Client Name",
                "Date",
                "Staff Name",
                "Facility Name",
                "Time",
                "Description",
              ];
              return (
                item.field_type === "Custom Field" &&
                // ||item.field_type === "Field"
                hasValidContent(item.field) &&
                !excludedFields.includes(item.field)
              );
            })
            ?.map((item, index) => {
              let fieldValue = entry?.fields?.[item.field] ?? "None";
              const isMultipleOptions = item?.options === "multipleOptions";

              // ✅ Utility to check if a value looks like a Date or Time
              const isDateLike = (val) =>
                typeof val === "string" &&
                (moment(val, moment.ISO_8601, true).isValid() ||
                  /\d{4}-\d{2}-\d{2}/.test(val));

              const isTimeLike = (val) =>
                typeof val === "string" &&
                (moment(
                  val,
                  ["HH:mm", "HH:mm:ss", "hh:mm A"],
                  true
                ).isValid() ||
                  /^\d{1,2}:\d{2}(\s?[APMapm]{2})?$/.test(val));

              // ✅ Format date or time fields automatically
              if (isDateLike(fieldValue)) {
                const date = moment(fieldValue);
                if (date.isValid()) {
                  fieldValue = date.format("MM/DD/YYYY");
                }
              } else if (isTimeLike(fieldValue)) {
                const time = moment(fieldValue, [
                  "HH:mm",
                  "HH:mm:ss",
                  "hh:mm A",
                ]);
                if (time.isValid()) {
                  fieldValue = time.format("hh:mm A");
                }
              }

              return (
                <View
                  key={index}
                  style={
                    isMultipleOptions
                      ? styles.contentSection
                      : styles.outcomeSection
                  }
                >
                  <View
                    style={
                      isMultipleOptions ? styles.sectionRow : styles.outcomeRow
                    }
                  >
                    <Text
                      style={
                        isMultipleOptions
                          ? styles.sectionLabel
                          : styles.outcomeLabel
                      }
                    >
                      {item.field}
                    </Text>

                    {isMultipleOptions ? (
                      // 🔹 Render multiple choice options as buttons
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        {Array.isArray(fieldValue) && fieldValue.length > 0 ? (
                          fieldValue.map((option, i) => (
                            <View
                              key={i}
                              style={styles.multipleOptionsContainer}
                            >
                              <Text style={styles.multipleOptionsText}>
                                {option}
                              </Text>
                            </View>
                          ))
                        ) : (
                          <Text style={styles.sectionContent}>None</Text>
                        )}
                      </View>
                    ) : (
                      <Text
                        style={
                          isMultipleOptions
                            ? styles.sectionContent
                            : styles.outcomeContent
                        }
                      >
                        {hasValidContent(fieldValue) ? fieldValue : "None"}
                      </Text>
                    )}
                  </View>
                </View>
              );
            })}

        {/* Medication Note Section */}
        {hasValidContent(entry.fields?.Medication) && (
          <View style={styles.contentSection}>
            <View style={styles.sectionRow}>
              <Text style={styles.sectionLabel}>Medication</Text>
            </View>
            <View style={styles.sectionRow}>
              <Text style={styles.sectionContent}>
                {entry.fields?.Medication || "None"}
              </Text>
            </View>
          </View>
        )}

        {/* Description/Progress note Section */}
        {entry?.descriptions && entry.descriptions.length > 0 && (
          <View>
            {entry.descriptions.map((item, goalIndex) => (
              <View key={item?.id || goalIndex}>
                {hasValidContent(item?.details?.outcomeNumber) && (
                  <View style={styles.outcomeSection}>
                    <View style={styles.outcomeRow}>
                      <Text style={styles.outcomeLabel}>Outcome Number</Text>
                      <Text style={styles.outcomeContent}>
                        {item?.details?.outcomeNumber || "None"}
                      </Text>
                    </View>
                  </View>
                )}
                {/* Outcome Title */}
                {hasValidContent(item?.details?.outcomeTitle) && (
                  <View style={styles.outcomeSection}>
                    <View style={styles.outcomeRow}>
                      <Text style={styles.outcomeLabel}>Outcome Title</Text>
                      <Text style={styles.outcomeContent}>
                        {item?.details?.outcomeTitle || "None"}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Description */}
                {hasValidContent(item.description) && (
                  <View style={styles.contentSection}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionLabel}>Description</Text>
                    </View>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionContent}>
                        {item?.description || "None"}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Essential Support Section */}
        {entry?.essentialSupport && entry.essentialSupport.length > 0 && (
          <View>
            {entry?.essentialSupport?.map((item, goalIndex) => {
              const matchedActivities =
                Array.isArray(item?.activities) &&
                item.activities.filter(
                  (act) => act?.id === item?.essentialActivityNumber
                );

              const hasMatchedActivities = matchedActivities?.length > 0;

              return (
                <View key={item?.id || goalIndex}>
                  {/* Essential Number */}
                  {hasValidContent(item?.essentialNumber) && (
                    <View style={styles.outcomeSection}>
                      <View style={styles.outcomeRow}>
                        <Text style={styles.outcomeLabel}>
                          Essential Number
                        </Text>
                        <Text style={styles.outcomeContent}>
                          {item?.essentialNumber || "None"}
                        </Text>
                      </View>
                    </View>
                  )}
                  {/* Essential Support Type */}
                  {hasValidContent(item?.essentialSupportType) && (
                    <View style={styles.outcomeSection}>
                      <View style={styles.outcomeRow}>
                        <Text style={styles.outcomeLabel}>
                          Essential Support Type
                        </Text>
                        <Text style={styles.outcomeContent}>
                          {item?.essentialSupportType || "None"}
                        </Text>
                      </View>
                    </View>
                  )}

                  {hasMatchedActivities &&
                    matchedActivities?.map(
                      ({ howOftenEssentialSupport, supportStatement }, idx) => (
                        <View style={styles.outcomeSection} key={idx}>
                          <View style={styles.outcomeRow}>
                            <Text
                              style={[
                                styles.outcomeLabel,
                                styles.essentialActAndFreq,
                              ]}
                            >
                              Frequency
                            </Text>
                            <Text
                              style={[
                                styles.outcomeContent,
                                styles.essentialActAndFreq,
                              ]}
                            >
                              {howOftenEssentialSupport ||
                                item?.howOftenEssentialSupport ||
                                "None"}
                            </Text>
                          </View>
                          <View style={styles.outcomeRow}>
                            <Text
                              style={[
                                styles.outcomeLabel,
                                styles.essentialActAndFreq,
                                { padding: "0 12 8 12" },
                              ]}
                            >
                              Essential Support Activity
                            </Text>
                            <Text
                              style={[
                                styles.outcomeContent,
                                styles.essentialActAndFreq,
                                { padding: "0 12 8 12" },
                              ]}
                            >
                              {supportStatement || "None"}
                            </Text>
                          </View>
                        </View>
                      )
                    )}

                  {/* Description */}
                  {/* {hasValidContent(item.description) && ( */}
                  {!!item?.description?.trim() && (
                    <View style={styles.contentSection}>
                      <View style={styles.sectionRow}>
                        <Text style={styles.sectionLabel}>Description</Text>
                      </View>
                      <View style={styles.sectionRow}>
                        <Text style={styles.sectionContent}>
                          {item?.description || "None"}
                        </Text>
                      </View>
                    </View>
                  )}

                  {/* Essential Support Type */}
                  {hasValidContent(item?.essentialSupportType) && (
                    <View style={styles.outcomeSection}>
                      <View style={styles.outcomeRow}>
                        <Text style={styles.outcomeLabel}>
                          Essential Support Type
                        </Text>
                        <Text style={styles.outcomeContent}>
                          {item?.essentialSupportType || "None"}
                        </Text>
                      </View>
                    </View>
                    // </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* Goals Section */}
        {entry?.goals && entry.goals.length > 0 && (
          <View>
            {entry.goals.map((goal, goalIndex) => (
              <View key={goal?.id || goalIndex}>
                {/* Goal Number */}
                {hasValidContent(goal?.goalNumber) && (
                  <View style={styles.outcomeSection}>
                    <View style={styles.outcomeRow}>
                      <Text style={styles.outcomeLabel}>Goal Number</Text>
                      <Text style={styles.outcomeContent}>
                        {goal?.goalNumber || "None"}
                      </Text>
                    </View>
                  </View>
                )}
                {/* Outcome/Goal */}
                {hasValidContent(goal?.identifiedGoal) && (
                  <View style={styles.outcomeSection}>
                    <View style={styles.outcomeRow}>
                      <Text style={styles.outcomeLabel}>Outcome/Goal</Text>
                      <Text style={styles.outcomeContent}>
                        {goal?.identifiedGoal || "None"}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Behavior */}
                {hasValidContent(goal.behaviorDescription) && (
                  <View style={styles.contentSection}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionLabel}>Behavior</Text>
                    </View>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionContent}>
                        {goal?.behaviorDescription || "None"}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Response */}
                {hasValidContent(goal.responseDescription) && (
                  <View style={styles.contentSection}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionLabel}>Response</Text>
                    </View>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionContent}>
                        {goal?.responseDescription || "None"}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Plan */}
                {hasValidContent(goal.planDescription) && (
                  <View style={styles.contentSection}>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionLabel}>Plan</Text>
                    </View>
                    <View style={styles.sectionRow}>
                      <Text style={styles.sectionContent}>
                        {goal?.planDescription || "None"}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Shift and Staff Info */}
        <View style={styles.lastSection}>
          <View style={styles.ShiftInfoContainer}>
            <View style={styles.shiftInfo}>
              <Text style={styles.staffAndShiftLabel}>Shift:</Text>
              <Text style={styles.staffName}>
                {entry?.fields["Shift"] || "None"}
              </Text>
            </View>
            {isCustomNote && hasValidContent(entry?.fields["Time"]) && (
              <View style={styles.shiftInfo}>
                <Text style={styles.staffAndShiftLabel}>Time:</Text>
                <Text style={styles.staffName}>
                  {moment(entry?.fields["Time"], "HH:mm:ss").format(
                    "hh:mm A"
                  ) || "None"}
                </Text>
              </View>
            )}
          </View>
          <View style={styles.staffInfo}>
            <Text style={styles.staffAndShiftLabel}>Staff:</Text>
            <Text style={styles.staffName}>
              {allStaffs?.find(
                (staff) => staff.id === entry?.fields?.["Staff Name"]
              )?.name || "None"}
            </Text>
            {(signatureData?.signature ||
              signatureData?.signatureImage ||
              signatureData?.signatureText) && (
              <View style={styles.signature}>
                {signatureData?.signature ||
                signatureData?.signatureImage ||
                signatureData?.image ? (
                  <Image
                    src={
                      signatureData?.image ||
                      (signatureData?.signature &&
                        decrypt(signatureData?.signature)) ||
                      (signatureData?.signatureImage &&
                        decrypt(signatureData?.signatureImage))
                    }
                    style={styles.signatureImage}
                  />
                ) : null}
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverWrapper}>
          <Image
            src="/assets/images/note-report-cover.jpg"
            style={styles.coverBg}
          />

          {/* Overlay Text */}
          <View style={styles.coverContent}>
            <View style={styles.coverFacility}>
              <Text style={styles.coverFacilityLabel}>Facility:</Text>
              <Text style={styles.coverFacilityName}>
                {reportInfo?.facilityName || "None"}
              </Text>
            </View>
            <View style={styles.coverFacility}>
              <Text style={styles.coverFacilityLabel}>Report Date:</Text>
              <Text style={styles.coverFacilityName}>
                {reportInfo?.reportPeriod || "None"}
              </Text>
            </View>
          </View>
        </View>
      </Page>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src="/assets/images/logo2.png" style={styles.logo} />
          <Text style={styles.reportTitle}>REPORT</Text>
          <Text style={styles.reportPeriod}>({reportInfo?.reportPeriod})</Text>
          <Text style={styles.entryCount}>
            {noteEntries?.length || 0} Note Entries
          </Text>
        </View>

        {/* Note Entries */}
        {noteEntries?.map((entry, index) => renderNoteEntry(entry, index))}
      </Page>
    </Document>
  );
};

export default NoteReportPDF;
